import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { black } from "designSystem/styles/color";
import FlexTopDrawer from "designSystem/components/flex/FlexTopDrawer";
import { CSS_ANCHOR_RESET, navListStyle } from "designSystem/styles/style-functions";
import OverflowText from "designSystem/components/text/OverflowText";
import { HOVER } from "designSystem/theme";
import { TooltipProps } from "designSystem/components/tooltip/Tooltip";
import { useStore } from "csi/state/hooks";
import { LayoutItem } from "csi/state/layout";
import { debounce } from "lodash";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent } from "react";

const PREFIX = "LayoutNavigator";

const classes = {
	depth1: `${PREFIX}depth1`,
	depth2: `${PREFIX}depth2`,
	depth3: `${PREFIX}depth3`,
	depth4: `${PREFIX}depth4`,
	group: `${PREFIX}group`,
	leaf: `${PREFIX}leaf`,
	list: `${PREFIX}list`,
};

const StyledFlexTopDrawer = styled(FlexTopDrawer)((
	{
		theme,
	},
) => ({
	[`& .${classes.depth1}`]: {
		paddingLeft: theme.spacing(2),
	},

	[`& .${classes.depth2}`]: {
		paddingLeft: theme.spacing(4),
	},

	[`& .${classes.depth3}`]: {
		paddingLeft: theme.spacing(6),
	},

	[`& .${classes.depth4}`]: {
		paddingLeft: theme.spacing(8),
	},

	[`& .${classes.group}`]: {
		color: theme.palette.grey.A100,
		display: "flex",
		alignItems: "center",

		[HOVER]: {
			color: black[300],
			cursor: "pointer",
		},
	},

	[`& .${classes.leaf}`]: navListStyle(theme),

	[`& .${classes.list}`]: {
		paddingBottom: theme.spacing(2),
		paddingTop: theme.spacing(2),
	},
}));

interface LayoutNavgatorItemProps {
	item: LayoutItem;
}

const debouncedResolve = debounce((layoutStore, item) => {
	layoutStore.resolveItems([item]);
}, 250);

export const LayoutNavigatorItem: FunctionComponent<LayoutNavgatorItemProps> = observer((props) => {
	const { layoutStore } = useStore();
	const { getItemHref, isParentOfSelectedItem, selectedItem } = layoutStore;
	const { item } = props;
	const { cache, children, collapsed, depth, leaf, name, node, setCollapsed, urlPath, visible } = item;
	const collapsedGroup = collapsed && !isParentOfSelectedItem(item);
	const indentClass = classes[`depth${depth}`];
	const tooltipProps: Partial<TooltipProps> = { placement: "right" };

	if (!node) {
		const paths = cache.getUnresolvedUrlPaths([urlPath]);
		if (paths.length) {
			debouncedResolve(layoutStore, item);
		}
	}

	return leaf || !visible ? (
		<ListItem
			className={`${CSS_ANCHOR_RESET} ${classes.leaf}`}
			component="a"
			href={getItemHref(item)}
			selected={selectedItem === item}
			style={visible ? null : { display: "none" }}
		>
			<OverflowText
				className={indentClass}
				text={name}
				tooltipProps={tooltipProps}
			/>
		</ListItem>
	)
		: (
			<Fragment>
				<ListItem
					aria-label={`${collapsedGroup ? "expand" : "collapse"} ${name}`}
					className={classes.group}
					onClick={() => setCollapsed(!collapsedGroup)}
				>
					<OverflowText
						bold
						className={indentClass}
						grow
						text={name}
						tooltipProps={tooltipProps}
						uppercase
					/>
					<Box height={16} ml={1}>
						{collapsedGroup ? <ExpandMore /> : <ExpandLess />}
					</Box>
				</ListItem>
				<Collapse in={!collapsedGroup} timeout="auto">
					{
						children.map((item) =>
							<LayoutNavigatorItem item={item} key={item.uniqueId} />,
						)
					}
				</Collapse>
			</Fragment>
		);
});

export const LayoutNavigator = observer(() => {
	const { layoutStore: { rootItem } } = useStore();

	return (
		<StyledFlexTopDrawer disabled open>
			{{
				top:
					<Box p={2}>
						TODO: Search Box
					</Box>,

				bottom:
					<List className={classes.list}>
						{
							rootItem.children.map((item) =>
								<LayoutNavigatorItem item={item} key={item.uniqueId} />,
							)
						}
					</List>,

			}}
		</StyledFlexTopDrawer>
	);
});

export default LayoutNavigator;
